import { useEffect, useState } from "react";
import { CountdownCard } from "./CountdownCard";
import { CountdownEditor } from "./CountdownEditor";
import { CountdownEmptyState } from "./CountdownEmptyState";
import {
  removeCountdown,
  saveCountdown,
} from "./countdown.actions";
import {
  createCloudCountdown,
  deleteCloudCountdown,
  getCloudCountdowns,
  updateCloudCountdown,
} from "./countdown.repository";
import {
  loadCountdowns,
  saveCountdowns,
} from "./countdown.storage";
import type { Countdown } from "./countdown.types";
import "./CountdownPage.css";

export function CountdownPage() {
  const [countdowns, setCountdowns] = useState<Countdown[]>(
    () => loadCountdowns(),
  );

  const [editorOpen, setEditorOpen] = useState(false);

  const [editingCountdown, setEditingCountdown] =
    useState<Countdown | null>(null);

  const [loadingCloud, setLoadingCloud] = useState(true);

  const [syncMessage, setSyncMessage] = useState("");

  useEffect(() => {
    saveCountdowns(countdowns);
  }, [countdowns]);

  useEffect(() => {
    async function restoreCloudCountdowns() {
      try {
        const cloudCountdowns = await getCloudCountdowns();

        setCountdowns(cloudCountdowns);
        setSyncMessage("");
      } catch {
        setSyncMessage(
          "Showing saved device countdowns. Cloud sync will retry when available.",
        );
      } finally {
        setLoadingCloud(false);
      }
    }

    void restoreCloudCountdowns();
  }, []);

  function openNewCountdown() {
    setEditingCountdown(null);
    setEditorOpen(true);
  }

  function openEditCountdown(countdown: Countdown) {
    setEditingCountdown(countdown);
    setEditorOpen(true);
  }

  function closeEditor() {
    setEditorOpen(false);
    setEditingCountdown(null);
  }

  async function handleSave(countdown: Countdown) {
    const previous = countdowns;

    setCountdowns((current) =>
      saveCountdown(current, countdown),
    );

    closeEditor();

    try {
      const saved = countdown.cloudId
        ? await updateCloudCountdown(countdown)
        : await createCloudCountdown(countdown);

      setCountdowns((current) =>
        saveCountdown(
          removeCountdown(current, countdown.id),
          saved,
        ),
      );

      setSyncMessage("Saved to SARADIUM cloud.");
    } catch (error) {
      setCountdowns(previous);

      setSyncMessage(
        error instanceof Error
          ? error.message
          : "Countdown could not be saved to cloud.",
      );
    }
  }

  async function handleDelete(countdownId: string) {
    const target = countdowns.find(
      (countdown) => countdown.id === countdownId,
    );

    if (!target) {
      return;
    }

    const previous = countdowns;

    setCountdowns((current) =>
      removeCountdown(current, countdownId),
    );

    try {
      await deleteCloudCountdown(target);
      setSyncMessage("Countdown deleted.");
    } catch (error) {
      setCountdowns(previous);

      setSyncMessage(
        error instanceof Error
          ? error.message
          : "Countdown could not be deleted.",
      );
    }
  }

  return (
    <>
      <main className="countdown-page">
        <header className="countdown-page__header">
          <div>
            <span className="countdown-page__eyebrow">
              LOOK FORWARD
            </span>

            <h1>Countdowns</h1>

            <p>
              Your upcoming moments in one place.
            </p>
          </div>

          <button
            className="countdown-page__add-button"
            type="button"
            aria-label="Add countdown"
            onClick={openNewCountdown}
          >
            +
          </button>
        </header>

        <div className="countdown-page__sync-row">
          <p className="countdown-page__section-title">
            Your Countdowns
          </p>

          {loadingCloud && (
            <span className="countdown-page__syncing">
              Syncing…
            </span>
          )}
        </div>

        {syncMessage && (
          <p className="countdown-page__sync-message">
            {syncMessage}
          </p>
        )}

        {countdowns.length === 0 ? (
          <CountdownEmptyState
            onAdd={openNewCountdown}
          />
        ) : (
          <section className="countdown-page__list">
            {countdowns.map((countdown) => (
              <CountdownCard
                key={countdown.id}
                countdown={countdown}
                onEdit={openEditCountdown}
                onDelete={handleDelete}
              />
            ))}
          </section>
        )}
      </main>

      <CountdownEditor
        open={editorOpen}
        countdown={editingCountdown}
        onClose={closeEditor}
        onSave={(countdown) => {
          void handleSave(countdown);
        }}
      />
    </>
  );
}